import { Module } from '../../types';

export const dspyModule5: Module = {
  id: 'dspy-module5',
  title: 'Advanced DSPy Features',
  description:
    'Explore advanced programming patterns, caching, observability, and working with different data types.',
  duration: '4-5 hours',
  isUnderDevelopment: true,
  content: `# Module 5: Advanced DSPy Features

## 🎯 Module Overview
This module explores advanced DSPy features that enable production-ready, high-performance AI systems. You'll learn about assertions for ensuring output quality, parallel and async execution for performance, caching strategies, comprehensive observability, and handling complex data types including images.

## 🎯 Learning Objectives
- Implement DSPy Assertions to enforce constraints and improve reliability
- Build high-performance systems with parallel and async execution
- Configure and customize caching for optimal performance
- Set up comprehensive observability and debugging workflows
- Work with multi-modal inputs including images and complex data structures
- Master streaming for real-time responsive applications

---

## 🔧 5.1 Advanced Programming Patterns

### DSPy Assertions for Constraints

DSPy Assertions provide a powerful mechanism to automatically enforce constraints on language model outputs, ensuring reliability and correctness without manual prompt engineering.

#### Understanding Assertions vs Suggestions

\`\`\`python
import dspy
from dspy.primitives.assertions import assert_transform_module

# Two types of constraints:
# 1. dspy.Assert - Hard constraint, stops execution on failure
# 2. dspy.Suggest - Soft constraint, logs but continues

class ConstrainedQA(dspy.Module):
    def __init__(self):
        self.answer = dspy.ChainOfThought("question -> answer")
    
    def forward(self, question):
        # Generate answer
        result = self.answer(question=question)
        
        # Hard constraint: Answer must be concise
        dspy.Assert(
            len(result.answer) <= 200,
            "Answer must be under 200 characters"
        )
        
        # Soft constraint: Prefer confident answers
        dspy.Suggest(
            "I don't know" not in result.answer.lower(),
            "Try to provide a definitive answer when possible"
        )
        
        return result

# Transform module to enable assertions
qa_with_assertions = assert_transform_module(ConstrainedQA())

# Or use the convenience method
qa_with_assertions = ConstrainedQA().activate_assertions()
\`\`\`

#### How Assertions Work Internally

When a constraint fails, DSPy:
1. **Backtracks** to the failing module
2. **Modifies the signature** with error feedback
3. **Retries** with improved context
4. **Repeats** up to max_backtracking_attempts

\`\`\`python
# Custom backtracking configuration
from functools import partial
from dspy.primitives.assertions import backtrack_handler

qa_with_custom_backtrack = assert_transform_module(
    ConstrainedQA(),
    functools.partial(backtrack_handler, max_backtracks=3)
)
\`\`\`

#### Advanced Assertion Patterns

\`\`\`python
class AdvancedAssertions(dspy.Module):
    def __init__(self):
        self.extract = dspy.ChainOfThought("text -> entities: list[str]")
        self.summarize = dspy.ChainOfThought("text -> summary")
        self.previous_summaries = []
    
    def forward(self, text):
        # Extract entities with validation
        entities = self.extract(text=text)
        
        # Multiple constraints on entities
        dspy.Assert(
            len(entities.entities) > 0,
            "Must extract at least one entity"
        )
        
        dspy.Assert(
            all(len(entity) > 2 for entity in entities.entities),
            "Each entity must be at least 3 characters"
        )
        
        # Generate summary
        summary = self.summarize(text=text)
        
        # Custom validation function
        def validate_summary_uniqueness(summary_text, previous):
            """Check if summary is sufficiently different from previous ones."""
            for prev in previous:
                if similarity_score(summary_text, prev) > 0.9:
                    return False
            return True
        
        dspy.Suggest(
            validate_summary_uniqueness(summary.summary, self.previous_summaries),
            f"Summary too similar to previous summaries. Try a different perspective."
        )
        
        self.previous_summaries.append(summary.summary)
        
        return dspy.Prediction(
            entities=entities.entities,
            summary=summary.summary
        )
\`\`\`

#### Assertions with Complex Programs

\`\`\`python
class MultiHopQAWithAssertions(dspy.Module):
    def __init__(self, passages_per_hop=3, max_hops=2):
        self.generate_query = [
            dspy.ChainOfThought("context, question -> query")
            for _ in range(max_hops)
        ]
        self.retrieve = dspy.Retrieve(k=passages_per_hop)
        self.generate_answer = dspy.ChainOfThought(
            "context, question -> answer"
        )
        self.max_hops = max_hops
    
    def forward(self, question):
        context = []
        previous_queries = [question]
        
        for hop in range(self.max_hops):
            # Generate search query
            query = self.generate_query[hop](
                context=context,
                question=question
            ).query
            
            # Assertion: Query must be unique
            dspy.Assert(
                query not in previous_queries,
                f"Query must be different from: {'; '.join(previous_queries)}"
            )
            
            # Assertion: Query must be focused
            dspy.Assert(
                len(query.split()) <= 10,
                "Search query must be 10 words or less"
            )
            
            previous_queries.append(query)
            
            # Retrieve passages
            passages = self.retrieve(query).passages
            
            # Assertion: Must retrieve relevant content
            dspy.Assert(
                len(passages) > 0,
                "No passages retrieved. Try a different query."
            )
            
            context.extend(passages)
        
        # Generate final answer
        answer = self.generate_answer(
            context=context,
            question=question
        )
        
        # Assertion: Answer must cite sources
        dspy.Suggest(
            any(str(i+1) in answer.answer for i in range(len(context))),
            "Consider citing passage numbers in your answer"
        )
        
        return answer

# Activate assertions
qa_system = MultiHopQAWithAssertions().activate_assertions()
\`\`\`

### Multi-Stage Pipelines

Build complex workflows with multiple processing stages:

\`\`\`python
class DocumentAnalysisPipeline(dspy.Module):
    """Multi-stage document processing pipeline."""
    
    def __init__(self):
        # Stage 1: Document understanding
        self.classify = dspy.Predict(
            "document -> doc_type: Literal['technical', 'legal', 'financial', 'general']"
        )
        
        # Stage 2: Specialized processors
        self.technical_analyzer = dspy.ChainOfThought(
            "document -> key_concepts: list[str], complexity_score: float"
        )
        self.legal_analyzer = dspy.ChainOfThought(
            "document -> legal_entities: list[str], obligations: list[str]"
        )
        self.financial_analyzer = dspy.ChainOfThought(
            "document -> monetary_values: list[float], risk_factors: list[str]"
        )
        
        # Stage 3: Summary generation
        self.summarizer = dspy.ChainOfThought(
            "document, analysis -> executive_summary"
        )
        
        # Stage 4: Action items
        self.action_extractor = dspy.ChainOfThought(
            "document, analysis -> action_items: list[str], deadlines: list[str]"
        )
    
    def forward(self, document):
        # Stage 1: Classification
        doc_type = self.classify(document=document).doc_type
        
        # Stage 2: Specialized analysis
        if doc_type == "technical":
            analysis = self.technical_analyzer(document=document)
        elif doc_type == "legal":
            analysis = self.legal_analyzer(document=document)
        elif doc_type == "financial":
            analysis = self.financial_analyzer(document=document)
        else:
            # General analysis
            analysis = dspy.Prediction(insights=["General document"])
        
        # Stage 3: Generate summary
        summary = self.summarizer(
            document=document,
            analysis=str(analysis)
        )
        
        # Stage 4: Extract actions
        actions = self.action_extractor(
            document=document,
            analysis=str(analysis)
        )
        
        return dspy.Prediction(
            doc_type=doc_type,
            analysis=analysis,
            summary=summary.executive_summary,
            action_items=actions.action_items,
            deadlines=actions.deadlines
        )
\`\`\`

### Parallel Execution with \`dspy.Parallel\`

Process multiple inputs concurrently for better performance:

\`\`\`python
# Basic parallel execution
parallel_qa = dspy.Parallel(dspy.ChainOfThought("question -> answer"))

questions = [
    "What is quantum computing?",
    "Explain machine learning",
    "Define blockchain technology"
]

# Process all questions in parallel
results = parallel_qa(questions)

# Advanced parallel patterns
class ParallelAnalysis(dspy.Module):
    def __init__(self):
        self.sentiment = dspy.Predict("text -> sentiment")
        self.entities = dspy.Predict("text -> entities: list[str]")
        self.summary = dspy.ChainOfThought("text -> summary")
    
    def forward(self, texts):
        # Run different analyses in parallel
        sentiments = dspy.Parallel(self.sentiment)(texts)
        entities = dspy.Parallel(self.entities)(texts)
        summaries = dspy.Parallel(self.summary)(texts)
        
        # Combine results
        results = []
        for i, text in enumerate(texts):
            results.append(dspy.Prediction(
                text=text,
                sentiment=sentiments[i].sentiment,
                entities=entities[i].entities,
                summary=summaries[i].summary
            ))
        
        return results
\`\`\`

### Async Programming with \`asyncify\`

Convert DSPy modules for async execution:

\`\`\`python
import asyncio
import dspy

# Configure async-compatible LM
dspy.configure(lm=dspy.LM("openai/gpt-4o-mini"))

# Basic async usage
predict = dspy.Predict("question -> answer")

async def async_qa():
    # Use acall() for async execution
    result = await predict.acall(question="What is async programming?")
    return result

# Run async function
result = asyncio.run(async_qa())

# Advanced async patterns
class AsyncPipeline(dspy.Module):
    def __init__(self):
        self.understand = dspy.ChainOfThought("question -> key_concepts: list[str]")
        self.research = dspy.ChainOfThought("concepts -> detailed_info")
        self.synthesize = dspy.ChainOfThought("info -> final_answer")
    
    async def aforward(self, question):
        # Async execution of pipeline stages
        concepts = await self.understand.acall(question=question)
        
        # Parallel async research on each concept
        research_tasks = [
            self.research.acall(concepts=concept)
            for concept in concepts.key_concepts
        ]
        research_results = await asyncio.gather(*research_tasks)
        
        # Synthesize results
        combined_info = "\\n".join([r.detailed_info for r in research_results])
        final_answer = await self.synthesize.acall(info=combined_info)
        
        return final_answer

# Usage
async def main():
    pipeline = AsyncPipeline()
    result = await pipeline.acall(question="How do neural networks learn?")
    print(result.final_answer)

asyncio.run(main())
\`\`\`

### Streaming Responses

Implement real-time streaming for better user experience:

\`\`\`python
import dspy
import asyncio

# Enable streaming for specific fields
predict = dspy.Predict("question -> answer")
stream_predict = dspy.streamify(
    predict,
    stream_listeners=[
        dspy.streaming.StreamListener(signature_field_name="answer")
    ]
)

async def stream_example():
    output_stream = stream_predict(question="Explain quantum computing")
    
    # Process streamed chunks
    full_answer = ""
    async for chunk in output_stream:
        if isinstance(chunk, dspy.streaming.StreamResponse):
            print(chunk.chunk, end="", flush=True)
            full_answer += chunk.chunk
        elif isinstance(chunk, dspy.Prediction):
            # Final prediction object
            return chunk

# Advanced streaming with status updates
class StreamingWithStatus(dspy.Module):
    def __init__(self):
        self.research = dspy.Tool(self.search_web, name="web_search")
        self.answer = dspy.ChainOfThought("context, question -> answer")
    
    def search_web(self, query: str):
        # Simulate web search
        return f"Results for: {query}"
    
    def forward(self, question):
        context = self.research(query=question)
        return self.answer(context=context, question=question)

# Custom status messages
class MyStatusProvider(dspy.streaming.StatusMessageProvider):
    def tool_start_status_message(self, instance, inputs):
        return f"🔍 Searching web for: {inputs['query']}..."
    
    def tool_end_status_message(self, outputs):
        return "✅ Search complete!"
    
    def module_start_status_message(self, instance, inputs):
        return "🤔 Thinking..."

# Stream with status updates
streaming_qa = dspy.streamify(
    StreamingWithStatus(),
    stream_listeners=[
        dspy.streaming.StreamListener(signature_field_name="answer")
    ],
    status_message_provider=MyStatusProvider()
)
\`\`\`

---

## 📊 5.2 Caching and Performance

### Understanding DSPy's Cache Architecture

DSPy implements a three-layer caching system:

\`\`\`python
"""
Cache Layers:
1. In-memory (LRU) - Fast, limited size
2. On-disk (FanoutCache) - Persistent, larger capacity  
3. Server-side - Provider-managed (OpenAI, Anthropic)
"""

import dspy
import time

# Caching is enabled by default
dspy.configure(lm=dspy.LM("openai/gpt-4o-mini"))

predict = dspy.Predict("question -> answer")

# First call - hits the LM
start = time.time()
result1 = predict(question="What is the speed of light?")
print(f"First call: {time.time() - start:.2f}s")

# Second call - served from cache
start = time.time()
result2 = predict(question="What is the speed of light?")
print(f"Cached call: {time.time() - start:.4f}s")  # Much faster!
\`\`\`

### Cache Configuration

\`\`\`python
# Disable caching entirely
dspy.configure_cache(
    enable_disk_cache=False,
    enable_memory_cache=False
)

# Configure cache limits
dspy.configure_cache(
    enable_disk_cache=True,
    enable_memory_cache=True,
    disk_size_limit_bytes=1024 * 1024 * 1024,  # 1GB disk cache
    memory_max_entries=1000  # Max 1000 entries in memory
)

# Selective caching
class SelectiveCaching(dspy.Module):
    def __init__(self):
        # This will be cached
        self.static_classifier = dspy.Predict("text -> category")
        
        # This won't be cached (e.g., for dynamic content)
        self.dynamic_generator = dspy.ChainOfThought("context -> response")
    
    def forward(self, text, use_cache=True):
        # Classification uses cache
        category = self.static_classifier(text=text)
        
        # Generation can bypass cache
        if not use_cache:
            with dspy.context(cache=False):
                response = self.dynamic_generator(context=text)
        else:
            response = self.dynamic_generator(context=text)
        
        return dspy.Prediction(category=category, response=response)
\`\`\`

### Custom Cache Implementation

\`\`\`python
from hashlib import sha256
import ujson
from typing import Any, Optional

class CustomCache(dspy.clients.Cache):
    """Custom cache with special key generation logic."""
    
    def __init__(self, **kwargs):
        super().__init__(**kwargs)
        self.custom_storage = {}
    
    def cache_key(self, request: dict[str, Any], 
                  ignored_args_for_cache_key: Optional[list[str]] = None) -> str:
        """Generate cache key based only on message content."""
        # Extract just the messages, ignoring model/temperature/etc
        messages = request.get("messages", [])
        key_content = ujson.dumps(messages, sort_keys=True)
        return sha256(key_content.encode()).hexdigest()
    
    def get(self, request: dict[str, Any], 
            ignored_args_for_cache_key: Optional[list[str]] = None) -> Any:
        """Retrieve from cache."""
        key = self.cache_key(request, ignored_args_for_cache_key)
        return self.custom_storage.get(key)
    
    def put(self, request: dict[str, Any], value: Any,
            ignored_args_for_cache_key: Optional[list[str]] = None,
            enable_memory_cache: bool = True) -> None:
        """Store in cache."""
        if enable_memory_cache:
            key = self.cache_key(request, ignored_args_for_cache_key)
            self.custom_storage[key] = value

# Install custom cache
dspy.cache = CustomCache(
    enable_disk_cache=True,
    enable_memory_cache=True,
    disk_cache_dir="./my_cache"
)
\`\`\`

### Performance Optimization Strategies

\`\`\`python
class OptimizedPipeline(dspy.Module):
    """Performance-optimized DSPy pipeline."""
    
    def __init__(self):
        # Use lighter models for simple tasks
        self.classifier = dspy.Predict(
            "text -> category",
            model="gpt-3.5-turbo"  # Faster, cheaper for classification
        )
        
        # Heavier model for complex reasoning
        self.analyzer = dspy.ChainOfThought(
            "text, category -> analysis",
            model="gpt-4"
        )
        
        # Batch processor for efficiency
        self.batch_processor = dspy.Parallel(
            dspy.Predict("item -> processed_item")
        )
    
    def forward(self, texts: list[str]):
        # Strategy 1: Filter early
        categories = []
        for text in texts:
            cat = self.classifier(text=text).category
            categories.append(cat)
        
        # Strategy 2: Process only what's needed
        complex_texts = [
            t for t, c in zip(texts, categories) 
            if c in ["technical", "legal"]
        ]
        
        # Strategy 3: Batch processing
        if complex_texts:
            analyses = dspy.Parallel(self.analyzer)(
                [(t, c) for t, c in zip(complex_texts, categories) if c in ["technical", "legal"]]
            )
        
        # Strategy 4: Cache aggregation results
        return self._aggregate_results(texts, categories, analyses)
    
    @dspy.cache  # Method-level caching
    def _aggregate_results(self, texts, categories, analyses):
        """Aggregate results with caching."""
        # Expensive aggregation logic here
        return dspy.Prediction(
            summary="Aggregated results",
            details=analyses
        )
\`\`\`

### Cache Warming and Preloading

\`\`\`python
class CacheWarmer:
    """Preload cache with common queries."""
    
    def __init__(self, module):
        self.module = module
    
    def warm_cache(self, common_queries: list[str]):
        """Preload cache with common queries."""
        print("Warming cache...")
        
        for i, query in enumerate(common_queries):
            try:
                # Make the call to populate cache
                self.module(question=query)
                print(f"Cached {i+1}/{len(common_queries)}: {query[:50]}...")
            except Exception as e:
                print(f"Failed to cache: {query[:50]}... Error: {e}")
        
        print("Cache warming complete!")

# Usage
qa = dspy.ChainOfThought("question -> answer")
warmer = CacheWarmer(qa)

common_questions = [
    "What is machine learning?",
    "Explain neural networks",
    "What is deep learning?",
    # ... more common queries
]

warmer.warm_cache(common_questions)
\`\`\`

---

## 🔍 5.3 Observability and Debugging

### Using \`inspect_history\` for Basic Debugging

\`\`\`python
import dspy

# Enable history tracking
dspy.configure(lm=dspy.LM("openai/gpt-4o-mini"), trace=[])

# Run your program
qa = dspy.ChainOfThought("question -> answer")
result = qa(question="What causes rain?")

# Inspect the last N LM calls
dspy.inspect_history(n=1)

# More detailed inspection
def detailed_inspection():
    """Custom history analysis."""
    history = dspy.settings.trace
    
    for i, call in enumerate(history):
        print(f"\\n=== Call {i+1} ===")
        print(f"Timestamp: {call.get('timestamp', 'N/A')}")
        print(f"Model: {call.get('model', 'N/A')}")
        print(f"Input: {call.get('messages', [])[:100]}...")  # First 100 chars
        print(f"Output: {call.get('response', '')[:100]}...")
        print(f"Tokens: {call.get('usage', {})}")
\`\`\`

### MLflow Integration for Comprehensive Tracing

\`\`\`python
import mlflow
import dspy

# Setup MLflow
mlflow.set_tracking_uri("http://127.0.0.1:5000")
mlflow.set_experiment("DSPy-Advanced")

# Enable automatic DSPy tracking
mlflow.dspy.autolog()

# Complex pipeline with full observability
class ObservablePipeline(dspy.Module):
    def __init__(self):
        self.extract = dspy.ChainOfThought("text -> entities: list[str]")
        self.classify = dspy.Predict("entity -> entity_type")
        self.summarize = dspy.ChainOfThought("text, entities -> summary")
    
    def forward(self, text):
        with mlflow.start_run():
            # Log input
            mlflow.log_param("input_length", len(text))
            mlflow.log_param("input_preview", text[:100])
            
            # Extract entities
            entities_result = self.extract(text=text)
            mlflow.log_metric("num_entities", len(entities_result.entities))
            
            # Classify each entity
            classifications = []
            for entity in entities_result.entities:
                entity_type = self.classify(entity=entity)
                classifications.append({
                    "entity": entity,
                    "type": entity_type.entity_type
                })
            
            # Log classifications
            mlflow.log_dict(classifications, "classifications.json")
            
            # Generate summary
            summary = self.summarize(
                text=text,
                entities=entities_result.entities
            )
            
            # Log final output
            mlflow.log_text(summary.summary, "summary.txt")
            
            return dspy.Prediction(
                entities=entities_result.entities,
                classifications=classifications,
                summary=summary.summary
            )

# Run with full tracing
pipeline = ObservablePipeline()
result = pipeline(text="Apple Inc. announced new iPhone models in Cupertino.")
\`\`\`

### Custom Callbacks for Fine-Grained Control

\`\`\`python
from dspy.utils.callback import BaseCallback
import time
import json

class ComprehensiveLogger(BaseCallback):
    """Log everything that happens in the pipeline."""
    
    def __init__(self, log_file="pipeline.log"):
        self.log_file = log_file
        self.call_stack = []
        self.timings = {}
    
    def _log(self, event_type, data):
        """Write structured log entry."""
        entry = {
            "timestamp": time.time(),
            "event_type": event_type,
            "call_stack_depth": len(self.call_stack),
            "data": data
        }
        with open(self.log_file, "a") as f:
            f.write(json.dumps(entry) + "\\n")
    
    def on_module_start(self, call_id, instance, inputs):
        """Track module execution start."""
        self.call_stack.append({
            "call_id": call_id,
            "module": instance.__class__.__name__,
            "start_time": time.time()
        })
        
        self._log("module_start", {
            "module": instance.__class__.__name__,
            "inputs": str(inputs)[:200]  # Truncate long inputs
        })
    
    def on_module_end(self, call_id, outputs, exception):
        """Track module execution end."""
        if self.call_stack and self.call_stack[-1]["call_id"] == call_id:
            start_info = self.call_stack.pop()
            duration = time.time() - start_info["start_time"]
            
            self._log("module_end", {
                "module": start_info["module"],
                "duration": duration,
                "success": exception is None,
                "outputs": str(outputs)[:200] if outputs else None,
                "error": str(exception) if exception else None
            })
    
    def on_lm_start(self, call_id, instance, inputs):
        """Track LM calls."""
        self.timings[call_id] = time.time()
        
        self._log("lm_start", {
            "model": getattr(instance, "model", "unknown"),
            "prompt_preview": str(inputs)[:500]
        })
    
    def on_lm_end(self, call_id, outputs, exception):
        """Track LM responses."""
        duration = time.time() - self.timings.get(call_id, time.time())
        
        self._log("lm_end", {
            "duration": duration,
            "response_preview": str(outputs)[:500] if outputs else None,
            "tokens": outputs.get("usage", {}) if outputs else None,
            "error": str(exception) if exception else None
        })

# Install the callback
dspy.configure(callbacks=[ComprehensiveLogger("my_pipeline.log")])

# Run with comprehensive logging
qa = dspy.ChainOfThought("question -> answer")
result = qa(question="What is quantum entanglement?")

# Analyze logs
def analyze_logs(log_file):
    """Analyze performance from logs."""
    total_time = 0
    lm_calls = 0
    errors = 0
    
    with open(log_file, "r") as f:
        for line in f:
            entry = json.loads(line)
            if entry["event_type"] == "lm_end":
                total_time += entry["data"].get("duration", 0)
                lm_calls += 1
                if entry["data"].get("error"):
                    errors += 1
    
    print(f"Total LM time: {total_time:.2f}s")
    print(f"Total LM calls: {lm_calls}")
    print(f"Errors: {errors}")
\`\`\`

### Advanced Debugging Techniques

\`\`\`python
class DebugModule(dspy.Module):
    """Module with built-in debugging capabilities."""
    
    def __init__(self, signature, debug=True):
        self.predict = dspy.ChainOfThought(signature)
        self.debug = debug
        self.debug_info = []
    
    def forward(self, **kwargs):
        if self.debug:
            # Capture input state
            debug_entry = {
                "inputs": kwargs,
                "timestamp": time.time()
            }
            
            try:
                # Execute with monitoring
                result = self.predict(**kwargs)
                
                # Capture output state
                debug_entry["output"] = result
                debug_entry["success"] = True
                
                # Analyze quality
                if hasattr(result, "confidence"):
                    debug_entry["confidence"] = result.confidence
                
            except Exception as e:
                debug_entry["error"] = str(e)
                debug_entry["success"] = False
                raise
            
            finally:
                self.debug_info.append(debug_entry)
        else:
            result = self.predict(**kwargs)
        
        return result
    
    def get_debug_report(self):
        """Generate debugging report."""
        if not self.debug_info:
            return "No debug information available"
        
        report = f"Debug Report - {len(self.debug_info)} calls\\n"
        report += "=" * 50 + "\\n"
        
        for i, entry in enumerate(self.debug_info):
            report += f"\\nCall {i+1}:\\n"
            report += f"  Inputs: {entry['inputs']}\\n"
            report += f"  Success: {entry['success']}\\n"
            if 'error' in entry:
                report += f"  Error: {entry['error']}\\n"
            if 'confidence' in entry:
                report += f"  Confidence: {entry['confidence']}\\n"
        
        return report

# Usage
debug_qa = DebugModule("question -> answer", debug=True)
result = debug_qa(question="Complex question here")
print(debug_qa.get_debug_report())
\`\`\`

---

## 🖼️ 5.4 Working with Different Data Types

### Images with \`dspy.Image\`

\`\`\`python
import dspy
from PIL import Image as PILImage

# Configure with vision-capable model
dspy.configure(lm=dspy.LM("openai/gpt-4o"))

# Basic image analysis
class ImageAnalyzer(dspy.Module):
    def __init__(self):
        self.analyze = dspy.Predict(
            "image -> description: str, objects: list[str], scene_type: str"
        )
    
    def forward(self, image_path):
        # Load image
        image = dspy.Image(path=image_path)
        
        # Analyze
        result = self.analyze(image=image)
        
        return result

# Advanced multi-modal reasoning
class MultiModalQA(dspy.Module):
    def __init__(self):
        self.understand = dspy.ChainOfThought(
            "image, question -> visual_context: str"
        )
        self.answer = dspy.ChainOfThought(
            "visual_context, question -> answer"
        )
    
    def forward(self, image_path, question):
        # Create image object
        image = dspy.Image.from_file(image_path)
        
        # Extract visual context
        context = self.understand(image=image, question=question)
        
        # Generate answer
        answer = self.answer(
            visual_context=context.visual_context,
            question=question
        )
        
        return answer

# Working with multiple images
class ImageComparison(dspy.Module):
    def __init__(self):
        self.compare = dspy.ChainOfThought("""
            image1: dspy.Image, 
            image2: dspy.Image -> 
            similarities: list[str], 
            differences: list[str],
            recommendation: str
        """)
    
    def forward(self, image1_path, image2_path):
        img1 = dspy.Image.from_file(image1_path)
        img2 = dspy.Image.from_file(image2_path)
        
        return self.compare(image1=img1, image2=img2)
\`\`\`

### Complex Structured Outputs

\`\`\`python
from typing import List, Dict, Optional
from pydantic import BaseModel, Field

# Define complex output structures
class ProductAnalysis(BaseModel):
    name: str
    category: str
    price_range: str = Field(description="Low, Medium, or High")
    features: List[str]
    pros: List[str]
    cons: List[str]
    target_audience: List[str]
    overall_rating: float = Field(ge=1, le=5)

class MarketReport(BaseModel):
    market_size: float = Field(description="Estimated market size in billions")
    growth_rate: float = Field(description="Year-over-year growth percentage")
    key_players: List[str]
    trends: List[str]
    opportunities: List[str]
    risks: List[str]

# Use complex types in signatures
class MarketAnalyzer(dspy.Module):
    def __init__(self):
        self.analyze_product = dspy.ChainOfThought(
            "product_description: str -> analysis: ProductAnalysis"
        )
        
        self.generate_report = dspy.ChainOfThought(
            "analyses: list[ProductAnalysis], market_data: str -> report: MarketReport"
        )
    
    def forward(self, products: List[str], market_data: str):
        # Analyze each product
        analyses = []
        for product_desc in products:
            analysis = self.analyze_product(product_description=product_desc)
            analyses.append(analysis.analysis)
        
        # Generate market report
        report = self.generate_report(
            analyses=analyses,
            market_data=market_data
        )
        
        return report.report

# Nested structures
class ResearchNode(BaseModel):
    topic: str
    findings: List[str]
    confidence: float
    sub_topics: Optional[List['ResearchNode']] = None

ResearchNode.model_rebuild()  # Required for self-referential models

class RecursiveResearcher(dspy.Module):
    def __init__(self, max_depth=3):
        self.research = dspy.ChainOfThought(
            "topic: str, depth: int -> research: ResearchNode"
        )
        self.max_depth = max_depth
    
    def forward(self, topic: str, depth: int = 0):
        if depth >= self.max_depth:
            return None
        
        # Research current topic
        result = self.research(topic=topic, depth=depth)
        node = result.research
        
        # Recursively research subtopics
        if node.sub_topics and depth < self.max_depth - 1:
            for i, sub_topic in enumerate(node.sub_topics):
                if isinstance(sub_topic, str):
                    # Convert string to ResearchNode
                    sub_research = self.forward(sub_topic, depth + 1)
                    if sub_research:
                        node.sub_topics[i] = sub_research
        
        return node
\`\`\`

### Conversation History Management

\`\`\`python
class ConversationManager:
    """Manage conversation context efficiently."""
    
    def __init__(self, max_history=10):
        self.history = []
        self.max_history = max_history
        self.summary = None
    
    def add_turn(self, user_input: str, assistant_output: str):
        """Add a conversation turn."""
        self.history.append({
            "user": user_input,
            "assistant": assistant_output,
            "timestamp": time.time()
        })
        
        # Maintain history limit
        if len(self.history) > self.max_history:
            # Summarize old history before removing
            self._summarize_old_history()
            self.history = self.history[-self.max_history:]
    
    def _summarize_old_history(self):
        """Summarize conversation history that's being removed."""
        summarizer = dspy.ChainOfThought(
            "conversation: list[dict] -> summary: str"
        )
        
        old_turns = self.history[:-self.max_history]
        if old_turns:
            result = summarizer(conversation=old_turns)
            self.summary = result.summary
    
    def get_context(self) -> str:
        """Get formatted conversation context."""
        context_parts = []
        
        # Include summary if available
        if self.summary:
            context_parts.append(f"Previous conversation summary: {self.summary}")
        
        # Include recent history
        for turn in self.history:
            context_parts.append(f"User: {turn['user']}")
            context_parts.append(f"Assistant: {turn['assistant']}")
        
        return "\\n".join(context_parts)

class ConversationalAgent(dspy.Module):
    def __init__(self):
        self.respond = dspy.ChainOfThought(
            "conversation_history: str, user_input: str -> response: str"
        )
        self.conversation = ConversationManager()
    
    def forward(self, user_input: str):
        # Get conversation context
        context = self.conversation.get_context()
        
        # Generate response
        result = self.respond(
            conversation_history=context,
            user_input=user_input
        )
        
        # Update history
        self.conversation.add_turn(user_input, result.response)
        
        return result
\`\`\`

### Working with Examples and Predictions

\`\`\`python
# Creating rich examples for few-shot learning
def create_rich_examples():
    """Create examples with metadata and validation."""
    
    examples = []
    
    # Example with full annotations
    example1 = dspy.Example(
        question="What is the capital of France?",
        context=["France is a country in Western Europe...", 
                 "Paris is the capital and largest city..."],
        answer="Paris",
        confidence=1.0,
        metadata={
            "difficulty": "easy",
            "category": "geography",
            "verified": True
        }
    ).with_inputs("question", "context")
    
    examples.append(example1)
    
    # Example with validation
    example2 = dspy.Example(
        input_text="The quick brown fox jumps over the lazy dog",
        entities=["fox", "dog"],
        sentiment="neutral",
        word_count=9
    )
    
    # Add custom validation
    def validate_example(ex):
        # Check word count matches
        actual_count = len(ex.input_text.split())
        return actual_count == ex.word_count
    
    example2.is_valid = validate_example(example2)
    examples.append(example2)
    
    return examples

# Working with predictions
class PredictionProcessor:
    """Process and validate predictions."""
    
    @staticmethod
    def validate_prediction(pred: dspy.Prediction, 
                          expected_fields: List[str]) -> bool:
        """Validate prediction has expected fields."""
        for field in expected_fields:
            if not hasattr(pred, field):
                return False
        return True
    
    @staticmethod
    def merge_predictions(preds: List[dspy.Prediction]) -> dspy.Prediction:
        """Merge multiple predictions into one."""
        merged = {}
        
        for pred in preds:
            for key, value in pred.items():
                if key not in merged:
                    merged[key] = []
                merged[key].append(value)
        
        # Aggregate lists
        final = {}
        for key, values in merged.items():
            if all(isinstance(v, (int, float)) for v in values):
                # Numeric - take average
                final[key] = sum(values) / len(values)
            elif all(isinstance(v, str) for v in values):
                # Strings - take most common
                final[key] = max(set(values), key=values.count)
            else:
                # Other - keep as list
                final[key] = values
        
        return dspy.Prediction(**final)
\`\`\`

---

## 📋 Module 5 Summary

### What You've Learned

✅ **Advanced Programming Patterns**
- DSPy Assertions for automatic constraint enforcement
- Multi-stage pipelines with complex workflows
- Parallel execution for performance
- Async programming for scalability
- Real-time streaming responses

✅ **Caching and Performance**
- Three-layer cache architecture
- Cache configuration and customization
- Performance optimization strategies
- Cache warming and preloading

✅ **Observability and Debugging**
- Using inspect_history for basic debugging
- MLflow integration for comprehensive tracing
- Custom callbacks for fine-grained control
- Advanced debugging techniques

✅ **Working with Different Data Types**
- Multi-modal inputs with images
- Complex structured outputs with Pydantic
- Conversation history management
- Examples and predictions handling

### Quick Reference

\`\`\`python
# Assertions
dspy.Assert(constraint, "Error message")
dspy.Suggest(constraint, "Suggestion")
module.activate_assertions()

# Parallel/Async
parallel_module = dspy.Parallel(module)
await module.acall(**kwargs)

# Streaming
stream_module = dspy.streamify(
    module,
    stream_listeners=[...],
    status_message_provider=...
)

# Caching
dspy.configure_cache(
    enable_disk_cache=True,
    disk_size_limit_bytes=...,
    memory_max_entries=...
)

# Debugging
dspy.inspect_history(n=5)
mlflow.dspy.autolog()
dspy.configure(callbacks=[...])

# Images
image = dspy.Image(path="...")
image = dspy.Image.from_url("...")
image = dspy.Image.from_PIL(pil_image)
\`\`\`

### Best Practices

1. **Use Assertions Wisely**: Start with Suggest, upgrade to Assert when confident
2. **Cache Strategically**: Cache expensive operations, not dynamic content
3. **Monitor Everything**: Use MLflow in production for comprehensive observability
4. **Stream When Appropriate**: For user-facing applications with long responses
5. **Type Everything**: Use Pydantic models for complex data structures

### Common Pitfalls

1. **Assertion Loops**: Constraints that can never be satisfied
2. **Cache Pollution**: Caching user-specific or time-sensitive data
3. **Callback Overhead**: Too many callbacks slowing down execution
4. **Memory Leaks**: Not managing conversation history properly

### What's Next?

In Module 6, we'll build complete real-world applications:
- Classification systems with confidence scores
- Advanced question-answering with citations
- Agent systems with memory and tools
- Content generation pipelines

You now have mastered the advanced features that make DSPy powerful for production use!`,
  exercises: [
    {
      id: 'dspy-m5-ex1',
      title: 'Multi-Stage Pipeline with Assertions',
      description: 'Build a multi-stage pipeline with assertions and streaming',
      difficulty: 'hard',
      type: 'code',
      instructions:
        'Create a document processing pipeline that classifies documents, applies assertions, and streams results.',
      hints: [
        'Use multi-stage processing',
        'Apply assertions at each stage',
        'Implement streaming for the final output',
      ],
      solution: `import dspy
from dspy.primitives.assertions import assert_transform_module

class StreamingDocumentPipeline(dspy.Module):
    def __init__(self):
        self.classify = dspy.Predict("document -> doc_type")
        self.extract = dspy.ChainOfThought("document -> key_points: list[str]")
        self.summarize = dspy.ChainOfThought("document, key_points -> summary")
    
    def forward(self, document):
        # Classification with assertion
        doc_type = self.classify(document=document)
        dspy.Assert(
            doc_type.doc_type in ["technical", "business", "legal"],
            "Document must be classified as technical, business, or legal"
        )
        
        # Extract key points with validation
        extraction = self.extract(document=document)
        dspy.Assert(
            len(extraction.key_points) >= 3,
            "Must extract at least 3 key points"
        )
        
        # Generate summary
        summary = self.summarize(
            document=document,
            key_points=extraction.key_points
        )
        
        return dspy.Prediction(
            doc_type=doc_type.doc_type,
            key_points=extraction.key_points,
            summary=summary.summary
        )

# Enable assertions and streaming
pipeline = StreamingDocumentPipeline().activate_assertions()
streaming_pipeline = dspy.streamify(
    pipeline,
    stream_listeners=[
        dspy.streaming.StreamListener(signature_field_name="summary")
    ]
)`,
      testCases: [
        {
          input: 'Multi-stage pipeline with assertions and streaming',
          expected: true,
          description: 'Pipeline correctly implements stages, assertions, and streaming',
        },
      ],
    },
    {
      id: 'dspy-m5-ex2',
      title: 'Custom Cache for Semantic Similarity',
      description: 'Implement a custom cache that uses semantic similarity',
      difficulty: 'hard',
      type: 'code',
      instructions: 'Create a cache that groups semantically similar queries together.',
      hints: [
        'Use embeddings for similarity',
        'Implement custom cache_key method',
        'Handle similarity threshold',
      ],
      solution: `import dspy
from hashlib import sha256
import numpy as np
from sentence_transformers import SentenceTransformer

class SemanticCache(dspy.clients.Cache):
    """Cache using semantic similarity for key matching."""
    
    def __init__(self, similarity_threshold=0.85, **kwargs):
        super().__init__(**kwargs)
        self.embedder = SentenceTransformer('all-MiniLM-L6-v2')
        self.cache_embeddings = {}
        self.cache_data = {}
        self.similarity_threshold = similarity_threshold
    
    def _get_query_from_request(self, request):
        """Extract query text from request."""
        messages = request.get("messages", [])
        if messages and isinstance(messages[-1], dict):
            return messages[-1].get("content", "")
        return ""
    
    def _find_similar_key(self, query_embedding):
        """Find cached key with similar embedding."""
        max_similarity = 0
        best_key = None
        
        for key, cached_embedding in self.cache_embeddings.items():
            similarity = np.dot(query_embedding, cached_embedding) / (
                np.linalg.norm(query_embedding) * np.linalg.norm(cached_embedding)
            )
            if similarity > max_similarity and similarity >= self.similarity_threshold:
                max_similarity = similarity
                best_key = key
        
        return best_key
    
    def get(self, request, ignored_args_for_cache_key=None):
        """Retrieve using semantic similarity."""
        query = self._get_query_from_request(request)
        if not query:
            return None
        
        query_embedding = self.embedder.encode(query)
        similar_key = self._find_similar_key(query_embedding)
        
        if similar_key:
            return self.cache_data.get(similar_key)
        return None
    
    def put(self, request, value, ignored_args_for_cache_key=None, 
            enable_memory_cache=True):
        """Store with semantic key."""
        if not enable_memory_cache:
            return
        
        query = self._get_query_from_request(request)
        if not query:
            return
        
        # Generate unique key
        key = sha256(query.encode()).hexdigest()
        
        # Store embedding and data
        query_embedding = self.embedder.encode(query)
        self.cache_embeddings[key] = query_embedding
        self.cache_data[key] = value

# Install custom semantic cache
dspy.cache = SemanticCache(
    similarity_threshold=0.85,
    enable_disk_cache=True,
    enable_memory_cache=True
)`,
      testCases: [
        {
          input: 'Semantic cache with similarity matching',
          expected: true,
          description: 'Cache correctly implements semantic similarity matching',
        },
      ],
    },
    {
      id: 'dspy-m5-ex3',
      title: 'Comprehensive Debugging Framework',
      description: 'Create a debugging framework with MLflow integration',
      difficulty: 'hard',
      type: 'code',
      instructions: 'Build a framework that tracks all aspects of DSPy execution with MLflow.',
      hints: [
        'Use MLflow for tracking',
        'Implement custom callbacks',
        'Track metrics and artifacts',
      ],
      solution: `import mlflow
import dspy
from dspy.utils.callback import BaseCallback
import time
import json

class MLflowDebugger(BaseCallback):
    """Comprehensive debugging with MLflow."""
    
    def __init__(self, experiment_name="dspy-debug"):
        mlflow.set_experiment(experiment_name)
        self.active_runs = {}
        self.module_metrics = {}
    
    def on_module_start(self, call_id, instance, inputs):
        """Start MLflow run for module."""
        run = mlflow.start_run(nested=True)
        self.active_runs[call_id] = {
            "run": run,
            "module": instance.__class__.__name__,
            "start_time": time.time()
        }
        
        # Log module info
        mlflow.log_param(f"module_type", instance.__class__.__name__)
        mlflow.log_param(f"input_keys", list(inputs.keys()) if inputs else [])
        
        # Log input samples
        for key, value in (inputs or {}).items():
            if isinstance(value, str):
                mlflow.log_text(value[:1000], f"input_{key}.txt")
    
    def on_module_end(self, call_id, outputs, exception):
        """End MLflow run for module."""
        if call_id in self.active_runs:
            run_info = self.active_runs.pop(call_id)
            duration = time.time() - run_info["start_time"]
            
            # Log metrics
            mlflow.log_metric("duration_seconds", duration)
            mlflow.log_metric("success", 1 if exception is None else 0)
            
            # Log outputs
            if outputs and not exception:
                for key, value in outputs.items():
                    if isinstance(value, str):
                        mlflow.log_text(value[:1000], f"output_{key}.txt")
                    elif isinstance(value, (list, dict)):
                        mlflow.log_dict(value, f"output_{key}.json")
            
            # Log error if any
            if exception:
                mlflow.log_text(str(exception), "error.txt")
            
            # Update module metrics
            module_name = run_info["module"]
            if module_name not in self.module_metrics:
                self.module_metrics[module_name] = {
                    "calls": 0, "errors": 0, "total_time": 0
                }
            self.module_metrics[module_name]["calls"] += 1
            self.module_metrics[module_name]["total_time"] += duration
            if exception:
                self.module_metrics[module_name]["errors"] += 1
            
            mlflow.end_run()
    
    def on_lm_start(self, call_id, instance, inputs):
        """Track LM calls."""
        mlflow.log_metric("lm_calls", 1)
        mlflow.log_param("model", getattr(instance, "model", "unknown"))
    
    def on_lm_end(self, call_id, outputs, exception):
        """Track LM responses."""
        if outputs and "usage" in outputs:
            usage = outputs["usage"]
            mlflow.log_metric("total_tokens", usage.get("total_tokens", 0))
            mlflow.log_metric("prompt_tokens", usage.get("prompt_tokens", 0))
            mlflow.log_metric("completion_tokens", usage.get("completion_tokens", 0))
    
    def get_summary_report(self):
        """Generate summary report."""
        report = {
            "module_metrics": self.module_metrics,
            "timestamp": time.time()
        }
        mlflow.log_dict(report, "summary_report.json")
        return report

# Usage
debugger = MLflowDebugger("dspy-advanced-debug")
dspy.configure(callbacks=[debugger])

# Run pipeline with debugging
pipeline = dspy.ChainOfThought("question -> answer")
with mlflow.start_run():
    result = pipeline(question="What is quantum computing?")
    
    # Log summary
    summary = debugger.get_summary_report()
    print("Debug Summary:", summary)`,
      testCases: [
        {
          input: 'MLflow debugging framework with comprehensive tracking',
          expected: true,
          description: 'Framework correctly integrates MLflow for debugging',
        },
      ],
    },
    {
      id: 'dspy-m5-ex4',
      title: 'Multi-Modal Chatbot',
      description: 'Build a chatbot that handles text and images with conversation history',
      difficulty: 'hard',
      type: 'code',
      instructions:
        'Create a conversational agent that can process both text and images while maintaining context.',
      hints: [
        'Handle multiple input types',
        'Maintain conversation history',
        'Use vision models for images',
      ],
      solution: `import dspy
from typing import Union, Optional
import time

class MultiModalChatbot(dspy.Module):
    def __init__(self, max_history=10):
        # Configure for vision model
        self.text_responder = dspy.ChainOfThought(
            "conversation_history: str, user_input: str -> response: str"
        )
        self.image_analyzer = dspy.ChainOfThought(
            "image: dspy.Image, question: str -> visual_analysis: str"
        )
        self.multimodal_responder = dspy.ChainOfThought(
            "conversation_history: str, visual_analysis: str, user_input: str -> response: str"
        )
        
        # Conversation management
        self.history = []
        self.max_history = max_history
        self.context_summary = None
    
    def _update_history(self, user_input: str, assistant_response: str, 
                       image_analysis: Optional[str] = None):
        """Update conversation history."""
        entry = {
            "user": user_input,
            "assistant": assistant_response,
            "timestamp": time.time()
        }
        if image_analysis:
            entry["image_context"] = image_analysis
        
        self.history.append(entry)
        
        # Maintain history limit
        if len(self.history) > self.max_history:
            self._summarize_old_history()
            self.history = self.history[-self.max_history:]
    
    def _summarize_old_history(self):
        """Summarize conversation that's being removed."""
        if len(self.history) > self.max_history:
            summarizer = dspy.ChainOfThought(
                "conversation: list[dict] -> summary: str"
            )
            old_turns = self.history[:-self.max_history]
            result = summarizer(conversation=old_turns)
            self.context_summary = result.summary
    
    def _get_context(self) -> str:
        """Format conversation context."""
        parts = []
        
        if self.context_summary:
            parts.append(f"Previous context: {self.context_summary}")
        
        for turn in self.history:
            parts.append(f"User: {turn['user']}")
            if 'image_context' in turn:
                parts.append(f"[Image: {turn['image_context']}]")
            parts.append(f"Assistant: {turn['assistant']}")
        
        return "\\n".join(parts)
    
    def forward(self, user_input: str, image_path: Optional[str] = None):
        """Process user input with optional image."""
        context = self._get_context()
        
        if image_path:
            # Analyze image first
            image = dspy.Image.from_file(image_path)
            image_analysis = self.image_analyzer(
                image=image,
                question=user_input
            )
            
            # Generate response with image context
            response = self.multimodal_responder(
                conversation_history=context,
                visual_analysis=image_analysis.visual_analysis,
                user_input=user_input
            )
            
            # Update history with image context
            self._update_history(
                user_input,
                response.response,
                image_analysis.visual_analysis
            )
        else:
            # Text-only response
            response = self.text_responder(
                conversation_history=context,
                user_input=user_input
            )
            
            # Update history
            self._update_history(user_input, response.response)
        
        return dspy.Prediction(
            response=response.response,
            conversation_length=len(self.history),
            has_image_context=image_path is not None
        )
    
    def reset_conversation(self):
        """Reset conversation history."""
        self.history = []
        self.context_summary = None
        return "Conversation reset."

# Usage example
chatbot = MultiModalChatbot(max_history=10)

# Text conversation
response1 = chatbot("Hello! Can you help me analyze some data?")
print(response1.response)

# Image + text
response2 = chatbot(
    "What do you see in this chart?",
    image_path="chart.png"
)
print(response2.response)

# Continue conversation
response3 = chatbot("What trends do you notice?")
print(response3.response)`,
      testCases: [
        {
          input: 'Multi-modal chatbot with text and image handling',
          expected: true,
          description: 'Chatbot correctly handles both text and images with conversation history',
        },
      ],
    },
  ],
  quiz: {
    id: 'dspy-m5-quiz',
    title: 'Module 5: Advanced DSPy Features - Knowledge Check',
    description: 'Test your understanding of advanced DSPy features',
    passingScore: 6,
    questions: [
      {
        id: 'q1',
        question: 'What is the difference between dspy.Assert and dspy.Suggest?',
        choices: [
          'Assert logs warnings, Suggest throws errors',
          'Assert is for hard constraints that stop execution, Suggest is for soft constraints that continue',
          'Assert is faster than Suggest',
          'There is no difference',
        ],
        correctAnswer: 1,
        explanation:
          'dspy.Assert enforces hard constraints that stop execution on failure, while dspy.Suggest provides soft constraints that log but continue execution.',
      },
      {
        id: 'q2',
        question: 'How does DSPy handle assertion failures internally?',
        choices: [
          'It ignores them and continues',
          'It logs an error and exits',
          'It backtracks, modifies the signature with feedback, and retries',
          'It asks the user for input',
        ],
        correctAnswer: 2,
        explanation:
          'When an assertion fails, DSPy backtracks to the failing module, modifies the signature with error feedback, and retries up to max_backtracking_attempts.',
      },
      {
        id: 'q3',
        question: "What are the three layers of DSPy's cache architecture?",
        choices: [
          'Local, Remote, Cloud',
          'In-memory (LRU), On-disk (FanoutCache), Server-side',
          'Primary, Secondary, Tertiary',
          'Fast, Medium, Slow',
        ],
        correctAnswer: 1,
        explanation:
          'DSPy uses a three-layer cache: In-memory (LRU) for fast access, On-disk (FanoutCache) for persistence, and Server-side caching managed by providers.',
      },
      {
        id: 'q4',
        question: 'How do you enable streaming for specific fields in DSPy?',
        choices: [
          'Use stream=True parameter',
          'Use dspy.streamify with StreamListener for specific fields',
          'Call .stream() on the module',
          'Streaming is automatic',
        ],
        correctAnswer: 1,
        explanation:
          'Use dspy.streamify with StreamListener objects that specify which signature fields to stream.',
      },
      {
        id: 'q5',
        question: 'What is the purpose of MLflow integration in DSPy?',
        choices: [
          'To improve model performance',
          'To provide comprehensive tracing and observability',
          'To enable distributed training',
          'To reduce memory usage',
        ],
        correctAnswer: 1,
        explanation:
          'MLflow integration provides comprehensive tracing, logging, and observability for DSPy pipelines in production.',
      },
      {
        id: 'q6',
        question: 'How do you work with images in DSPy?',
        choices: [
          'Convert them to base64 strings',
          'Use the dspy.Image class with vision-capable models',
          'Images are not supported',
          'Use external image processing libraries',
        ],
        correctAnswer: 1,
        explanation:
          'DSPy provides a dspy.Image class for handling image inputs with vision-capable models like GPT-4V.',
      },
      {
        id: 'q7',
        question: 'What is the benefit of using Pydantic models with DSPy?',
        choices: [
          'Faster execution',
          'Better caching',
          'Type safety and validation for complex structured outputs',
          'Reduced token usage',
        ],
        correctAnswer: 2,
        explanation:
          'Pydantic models provide type safety, validation, and clear structure for complex outputs in DSPy signatures.',
      },
      {
        id: 'q8',
        question: 'How can you selectively disable caching for specific operations?',
        choices: [
          'Set cache=False in the module constructor',
          'Use dspy.context(cache=False) context manager',
          'Delete the cache files',
          'Caching cannot be selectively disabled',
        ],
        correctAnswer: 1,
        explanation:
          'Use the dspy.context(cache=False) context manager to temporarily disable caching for specific operations.',
      },
    ],
  },
};
